/**
 * GTM dataLayer helpers for first-time GitHub signup conversion tracking.
 * The backend appends `bs_new_github_signup=1` to the redirect URL for new accounts;
 * the dashboard reads it, pushes a one-time GTM event, then strips the param.
 *
 * The backend also appends a `bs_signup_cid=<uuid>` conversion ID. We forward
 * that value into the dataLayer event so the GTM-managed Reddit pixel can
 * include it as `conversion_id`. The same value is sent server-side via
 * Reddit's Conversions API so Reddit dedupes the two events.
 */

/** One-time conversion after first successful GitHub signup (backend sets URL query). */
export const GTM_GITHUB_FIRST_SIGNUP_SUCCESS_EVENT =
  "github_first_signup_success";

/** Must match `GithubLoginController::NEW_GITHUB_SIGNUP_QUERY_KEY`. */
export const GTM_NEW_GITHUB_SIGNUP_QUERY = "bs_new_github_signup";

/** Must match `GithubLoginController::SIGNUP_CONVERSION_ID_QUERY_KEY`. */
export const GTM_SIGNUP_CONVERSION_ID_QUERY = "bs_signup_cid";

type QueryRecord = Record<string, string | string[] | undefined>;

export function shouldEmitGithubFirstSignupGtm(query: QueryRecord): boolean {
  const raw = query[GTM_NEW_GITHUB_SIGNUP_QUERY];
  const val = Array.isArray(raw) ? raw[0] : raw;
  return val === "1";
}

export function readSignupConversionId(query: QueryRecord): string | undefined {
  const raw = query[GTM_SIGNUP_CONVERSION_ID_QUERY];
  const val = Array.isArray(raw) ? raw[0] : raw;
  return typeof val === "string" && val.length > 0 ? val : undefined;
}

export function emitGithubFirstSignupGtmEvent(conversionId?: string): void {
  if (typeof window === "undefined") {
    return;
  }
  window.dataLayer = window.dataLayer ?? [];
  const payload: Record<string, string> = {
    event: GTM_GITHUB_FIRST_SIGNUP_SUCCESS_EVENT,
  };
  if (conversionId) {
    payload.conversion_id = conversionId;
  }
  window.dataLayer.push(payload);
}

export function stripGithubFirstSignupQuery(query: QueryRecord): QueryRecord {
  const next = { ...query };
  delete next[GTM_NEW_GITHUB_SIGNUP_QUERY];
  delete next[GTM_SIGNUP_CONVERSION_ID_QUERY];
  return next;
}
